import type { CSSProperties } from 'react';
import { cx } from '@linaria/atomic';
import { motion, AnimatePresence } from 'motion/react';
import { WeaveThemeValues } from '../../../component-theme/theme-types';
import { useThemeValues } from '../../../hooks/use-theme-values';
import { TrayModalBackdrop } from '../atoms/modal-backdrop';
import { ModalProvider, type ModalProviderProps } from '../provider/modal.provider';
import type { ModalControlModalProps } from '../provider/use-modal-control';
import { TrayActions } from './tray-actions';
import { TrayBody } from './tray-body';
import { TrayHeader } from './tray-header';
import { tray, trayAdjustedHeight } from './tray.styles';

export type TrayProps = ModalControlModalProps &
  Pick<ModalProviderProps, 'mountTarget' | 'prepend'> & {
    animateOrigin?: 'right' | 'top';
    autoFocusTimeout?: number;
    children: React.ReactNode;
    width?: keyof typeof WIDTH_DIMS;
    disableCloseOnEscape?: boolean;
    showBackdrop?: boolean;
    backdropInsetLeft?: number | string;
    zIndex?: WeaveThemeValues['ZIndex'];
    className?: string;
    style?: React.CSSProperties;
  };

const trayClassname = 'wv-tray';

export const WIDTH_DIMS = {
  small: 360,
  smallXL: 400,
  smallXXL: 434,
  medium: 500,
  large: 600,
  xlarge: 700,
} as const;

const variants = (animateOrigin: TrayProps['animateOrigin']) => ({
  hidden: {
    opacity: 0,
    transform: animateOrigin === 'right' ? 'translate3d(5%, 0, 0)' : 'translate3d(0%, -100px, 0)',
  },
  visible: { opacity: 1, transform: 'translate3d(0%, 0, 0)' },
});

export const Tray = ({
  animateOrigin = 'right',
  autoFocusTimeout = 0,
  children,
  mountTarget,
  prepend,
  disableCloseOnEscape = false,
  showBackdrop = true,
  backdropInsetLeft,
  width = 'small',
  zIndex,
  onClose,
  show,
  style,
  className,
  ...rest
}: TrayProps) => {
  const { zIndex: zIndexDefault, heightOffset } = useThemeValues();
  const resolvedZIndex = zIndex ?? zIndexDefault.modals;
  const applyHeightOffset = !mountTarget && heightOffset > 0;

  return (
    <AnimatePresence>
      {show && (
        <ModalProvider
          timeout={autoFocusTimeout}
          onClose={onClose}
          className={trayClassname}
          mountTarget={mountTarget}
          prepend={prepend}
          disableCloseOnEscape={disableCloseOnEscape}
          show={show}
        >
          <TrayModalBackdrop
            showBackdrop={showBackdrop}
            onClick={() => onClose()} /* Optional: Remove if you don't want clicking outside to close */
            style={
              backdropInsetLeft != null
                ? {
                    left: typeof backdropInsetLeft === 'number' ? `${backdropInsetLeft}px` : backdropInsetLeft,
                  }
                : undefined
            }
            initial={{ opacity: 0 }}
            animate={{ opacity: show ? 1 : 0 }}
            exit={{ opacity: 0 }}
            variants={variants(animateOrigin)}
            zIndex={resolvedZIndex}
            omitOffset={!!mountTarget}
          />
          <motion.section
            className={cx(tray, applyHeightOffset && trayAdjustedHeight, className)}
            style={
              {
                '--tray-max-width': `${WIDTH_DIMS[width]}px`,
                zIndex: resolvedZIndex,
                ...(applyHeightOffset ? { '--tray-height-offset': `${heightOffset}px` } : {}),
                ...style,
              } as CSSProperties
            }
            {...rest}
            initial='hidden'
            animate={show ? 'visible' : 'hidden'}
            exit='hidden'
            variants={variants(animateOrigin)}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.section>
        </ModalProvider>
      )}
    </AnimatePresence>
  );
};

Tray.Header = TrayHeader;
Tray.Actions = TrayActions;
Tray.Body = TrayBody;
